import React, { useEffect, useRef, useState } from "react"

import {
  Faq,
  Text,
  extractTitle,
  getSectionImage,
  getSectionKind,
  Team,
  Posts,
  Gallery,
  Video,
  Quote,
  Contact,
  Features,
  Locations,
  PropertiesSlider,
} from "./blocks"

const SectionReveal = ({ children, index = 0 }) => {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined" || !ref.current) return undefined

    const observer = new window.IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          setIsVisible(true)
          observer.disconnect()
        })
      },
      {
        threshold: 0.14,
        rootMargin: "0px 0px -10% 0px",
      }
    )

    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  const delay = Math.min(index % 4, 3) * 70

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transform-gpu transition-all duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transform-none motion-reduce:transition-none ${
        isVisible
          ? "translate-y-0 scale-100 opacity-100"
          : "translate-y-3 scale-[0.994] opacity-0"
      }`}
    >
      {children}
    </div>
  )
}

// One gallery picture per chapter: the keyword scoring in Text.jsx otherwise
// returns the same best-scored image (typically the aerial view) for every
// "overview" chapter of a project page. Images already used by a previous
// chapter are withdrawn from the pool before the next pick.
const pickSectionImages = (content = [], sectionImages = []) => {
  const picks = new Map()
  if (!sectionImages.length) return picks
  const used = new Set()
  const imageKey = image => image?.url || image?.fluid?.src || ""
  content.forEach((item, index) => {
    if (item?.model?.apiKey !== "text") return
    const html = item?.textNode?.childMarkdownRemark?.html || ""
    const kind = getSectionKind(extractTitle(html))
    const pool = sectionImages.filter(image => !used.has(imageKey(image)))
    const image = getSectionImage({
      kind,
      sectionImages: pool.length ? pool : sectionImages,
      index,
    })
    if (image) {
      used.add(imageKey(image))
      picks.set(index, image)
    }
  })
  return picks
}

const Content = ({
  content,
  locale,
  propertyCardMode = "default",
  holidayMode = false,
  hideVideoBlocks = false,
  sectionImages = [],
  // Project `content` is already fetched for the page locale, but
  // gatsby-source-datocms reports the DEFAULT locale ("en") on the nested
  // blocks, so the locale guard below silently dropped every French chapter
  // (Harmonie FR, Lagoon Villas FR). Project pages opt out of that guard.
  ignoreBlockLocale = false,
}) => {
  const sectionImagePicks = pickSectionImages(content, sectionImages)
  return (
    <>
      {content.map((item, index) => {
        const block =
          !ignoreBlockLocale && item?.locale && locale && item.locale !== locale
            ? null
            : {
                features_section: <Features data={item} />,
                locations_section: <Locations data={item} />,
                posts_section: <Posts data={item} />,
                properties_slider: (
                  <PropertiesSlider
                    data={item}
                    cardMode={propertyCardMode}
                    holidayMode={holidayMode}
                  />
                ),
                gallery: <Gallery data={item} />,
                contact: <Contact data={item} />,
                quote: <Quote data={item} />,
                video: hideVideoBlocks ? null : <Video data={item} />,
                text: (
                  <Text
                    data={item}
                    locale={locale}
                    index={index}
                    sectionImages={sectionImages}
                    sectionImage={sectionImagePicks.get(index) || null}
                  />
                ),
                team: <Team data={item} />,
                faq: <Faq data={item} />,
              }[item.model && item.model.apiKey]

        if (!block) return null

        return (
          <SectionReveal key={index} index={index}>
            {block}
          </SectionReveal>
        )
      })}
    </>
  )
}

export default Content
